import { useFileInfo } from "@/api/services/global/file";
import { IconChevronLeft, IconChevronRight } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";
import { MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { background, divider, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { downloadFileFromPath } from "@/utils/export";
import { Box, Modal, Stack, styled, Typography, Button as ButtonMui, Divider, Grid } from "@mui/material";
import { FC, useState } from "react";
import { useCompanyFileSignedUrl } from "@/api/services/main/company";
import { convertByteToKb } from "@/utils/file";

type TImagePreviewModalProps = {
  currentFileIndex: number;
  filePath?: string;
  file?: File;
  imageList?: TExtendFile[];
  imagePaths: string[];
  open: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
};

export const ImagePreviewModal: FC<TImagePreviewModalProps> = ({ currentFileIndex, imageList, imagePaths, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(currentFileIndex);
  const [isDisableNext, setIsDisableNext] = useState(currentFileIndex === imagePaths.length - 1);
  const [isDisablePrev, setIsDisablePrev] = useState(currentFileIndex === 0);
  const [filePath, setFilePath] = useState(imagePaths[currentFileIndex]);
  const handleNext = () => {
    if (isDisableNext) return;
    if (imagePaths && imagePaths?.length > 0) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setFilePath(imagePaths[newIndex]);
      setIsDisableNext(newIndex === imagePaths.length - 1);
      setIsDisablePrev(newIndex === 0);
    }
  };

  const handlePrev = () => {
    if (isDisablePrev) return;
    if (imagePaths && imagePaths?.length > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setFilePath(imagePaths[newIndex]);
      setIsDisableNext(newIndex === imagePaths.length - 1);
      setIsDisablePrev(newIndex === 0);
    }
  };

  const currentFile = imageList?.[currentIndex];
  const { data: currentFileInfo } = useFileInfo(imageList?.length && imageList?.length > 0 ? currentFile?.filePath : filePath);
  const { data: currentFileSignedUrl } = useCompanyFileSignedUrl(imageList?.length && imageList?.length > 0 ? currentFile?.filePath : filePath);
  const handleDownloadFile = async () => {
    try {
      if (currentFile?.filePath || filePath) {
        await downloadFileFromPath(currentFile?.filePath || filePath || "");
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Stack>
          <Stack gap={2}>
            <FileName variant="sub16Bold" color={text.secondary}>
              {currentFile?.name ?? currentFileInfo?.data?.name}
            </FileName>
            {currentFileInfo && MIME_TYPES.IMAGE.includes(currentFileInfo.data.mimeType) ? (
              <Avatar filePath={currentFile?.filePath ?? filePath} width={800} height={500} />
            ) : (
              <iframe
                src={currentFileSignedUrl?.data.signedUrl}
                style={{ marginBottom: "20px", height: 500, width: 800, border: "none" }}
                title="PDF Page"
                allowFullScreen
              />
            )}
          </Stack>
          <Stack pt={2} gap={0.5}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  更新日時
                </Typography>
                <Divider />
                <Typography variant="body14">
                  {currentFileInfo?.data.updatedAt
                    ? dayjs(currentFileInfo?.data.updatedAt).format(DATE_TIME_FORMAT_WITH_SECONDS)
                    : EMPTY_STR.DATE_TIME}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  ファイル容量
                </Typography>
                <Divider />
                <Typography variant="body14">{convertByteToKb(currentFileInfo?.data?.size ?? 0)}kb</Typography>
              </Grid>
            </Grid>
          </Stack>
          <Stack py={2} flexDirection="row" gap={2} justifyContent="space-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCurrentIndex(0);
                onClose();
              }}
            >
              キャンセル
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadFile}>
              ダウンロード
            </Button>
          </Stack>
          <Stack pt={2} flexDirection="row" gap={2} justifyContent="center" alignItems="center">
            <BoxIcon onClick={handlePrev} style={{ opacity: !isDisablePrev ? 1 : 0.4, cursor: !isDisablePrev ? "pointer" : "not-allowed" }}>
              <IconChevronLeft width={20} height={20} color={text.primary} />
            </BoxIcon>
            <BoxIcon onClick={handleNext} style={{ opacity: !isDisableNext ? 1 : 0.4, cursor: !isDisableNext ? "pointer" : "not-allowed" }}>
              <IconChevronRight width={20} height={20} color={text.primary} />
            </BoxIcon>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 348px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 24px;
  max-height: 90vh;
`;

const FileName = styled(Typography)`
  max-width: 300px;
  ${textEllipsis};
  flex: 1;
`;

const BoxIcon = styled(ButtonMui)`
  padding: 0;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${divider.middle};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;
